/**
 * Copyright (C) 2018–2020 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import { LuiInputBase, LuiDomHelpers } from '@here/lui-base'
import scopedCss from 'lui-select.scss'
import template from 'lui-select.html'
import chevronopened from 'lui-icon-chevronup-onlight-solid-small.html'
import chevronclosed from 'lui-icon-chevrondown-onlight-solid-small.html'

export default class LuiSelect extends LuiInputBase {
  static get is() {
    return 'lui-select'
  }

  static get attributeToggles() {
    return ['disabled']
  }

  static get clickTriggers() {
    return ['arrowdown']
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  constructor() {
    super()
    // TODO add hidden and value to syncedProperties and implement logic in select and menu
    this.syncedProperties = ['selected', 'disabled']
  }

  init() {
    super.init()

    this.setAttribute('tabindex', 0)
    if (!this.chevronOpened) {
      const svgDiv = this._createChevron(chevronopened, 'chevronopened')
      this.appendChild(svgDiv)
    }
    if (!this.chevronClosed) {
      const svgDiv = this._createChevron(chevronclosed, 'chevronclosed')
      this.appendChild(svgDiv)
    }

    if (this.chevronOpened) this.chevronOpened.classList.add('lui-select__chevron')
    if (this.chevronClosed) this.chevronClosed.classList.add('lui-select__chevron')

    const _init = () => {
      // TODO Sync the selectedIndex from native <select>
      // TODO refactor the code below
      // Why the code below: Angular has changed the native <option> and <select>.
      // Setting the selected property of an option does not trigger an event
      if (this.select.selectedIndex === -1 && this.hasAttribute('placeholder')) {
        this.dialog.innerText = this.getAttribute('placeholder')
      }
    }

    const _isSelectAvailable = () => {
      return this._initialized && !!this.select
    }

    this.poll(_init, _isSelectAvailable)
  }

  onConnected() {
    super.onConnected()

    const _onConnected = () => {
      this.menu = this.generateMenu()
      this.appendChild(this._menu)
      this._wireNativeSelect(this.select)

      if (!this.disabled) {
        this._registerEvents()
      }

      this._update()
    }

    const _isSelectAvailable = () => {
      return this._initialized && !!this.select
    }

    this.poll(_onConnected, _isSelectAvailable)
  }

  disabledChanged() {
    super.disabledChanged()
    this.toggleCssClass('lui-disabled', this.disabled)

    const _disabledChanged = () => {
      if (this.disabled) {
        this._unregisterEvents()
      } else {
        this._registerEvents()
      }
    }

    const _isSelectAvailable = () => {
      return this._initialized && !!this.select
    }

    this.poll(_disabledChanged, _isSelectAvailable)
  }

  _registerEvents() {
    this._clickBound = this._clickBound || this._clickAction.bind(this)
    this._menuChangedBound = this._menuChangedBound || this._menuChangedAction.bind(this)
    this._menuOpenBound = this._menuOpenBound || this._menuOpenAction.bind(this)
    this._menuCloseBound = this._menuCloseBound || this._menuCloseAction.bind(this)
    this._componentChangedBound = this._componentChangedBound || this._componentChanged.bind(this)

    this.seedElement.addEventListener('click', this._clickBound)
    this.menu.addEventListener('change', this._menuChangedBound)
    this.menu.addEventListener('open', this._menuOpenBound)
    this.menu.addEventListener('close', this._menuCloseBound)

    if (this.select) {
      this.addEventListener('change', this._componentChangedBound)

      this._optionsObserver = window.lui.createMutationObserver(this._onMutationOptions.bind(this))
      this._optionsObserver.observe(this.select,
        {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: this.syncedProperties,

          // characterData: true,
          //  FIXME Angular falls into infinite loop if characterData is set to true
          //  The caus eis that Angular replaces original MutationObserver with its wrapper.
          //  The original one is accessible by: window[window.Zone.__symbol__("MutationObserver")]
          //  https://github.com/angular/angular/issues/26948
        })
    }
  }

  _makeFocusable() {
    if (!this.disabled) this.setAttribute('tabindex', 0)
  }

  _unregisterEvents() {
    this.seedElement.removeEventListener('click', this._clickBound)
    this.removeEventListener('change', this._componentChangedBound)

    if (this.menu) {
      this.menu.removeEventListener('change', this._menuChangedBound)
      this.menu.removeEventListener('open', this._menuOpenBound)
      this.menu.removeEventListener('close', this._menuCloseBound)
    }

    if (this._optionsObserver) {
      this._optionsObserver.disconnect()
    }
  }

  _setMenuPlaceholder(menu) {
    if (this.menuplaceholder !== null) {
      menu.setAttribute('placeholder', this.menuplaceholder)
    }
  }

  _componentChanged(event) {
    event.stopPropagation()
    event.preventDefault()
    this._syncSelectedIndex()
  }

  _onMutationOptions(mutations) {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length || mutation.removedNodes.length || mutation.type === 'attributes') {
        const newMenu = this.generateMenu()

        while (this.menu.firstChild) {
          this.menu.removeChild(this.menu.firstChild)
        }
        this.menu.append(...newMenu.querySelectorAll('lui-menuitem'))

        this._update()
      }
    })
  }

  _update() {
    this._syncSelectedIndex()
    this._updateSelectedItems()
    this._updateChevron()
  }

  onDisconnected() {
    super.onDisconnected()
    if (!this.disabled) {
      this._unregisterEvents()
    }
  }

  _menuOpenAction() {
    this._updateChevron()
  }

  _menuCloseAction() {
    this._updateChevron()

    this.delay(() => {
      // The delay avoids the menu looking focused while closing
      const scrollCache = LuiDomHelpers.getScrollPositions(this)
      this.focus()
      scrollCache.forEach(cacheEntry => {
        cacheEntry.node.scrollTo(cacheEntry.x, cacheEntry.y)
      })
    })
  }

  _clickAction(event) {
    if (event.target.matches('lui-menu *')) return
    if (!this.menu.open) this.menu.open = true
    this._updateSelectedItems()
  }

  _menuChangedAction() {
    this._ignoreChange = true
    this.select.options[this.menu.selectedIndex].selected = true
    this.select.dispatchEvent(new window.InputEvent('input'))
    this.select.dispatchEvent(new Event('change'))
    this.dispatchEvent(new window.InputEvent('input'))
    this.dispatchEvent(new Event('change'))
    this._updateSelectedItems()
  }

  _updateSelectedItems() {
    if (this.menu && this.menu.selectedIndex >= 0 && this.menu.selectedItems) {
      this._setDialogHtml(this.menu.selectedItems[0].innerHTML, this.menu.selectedItems[0].style.display === 'none')
    }
  }

  _syncSelectedIndex() {
    if (!this.menu || !this.select) {
      return
    }
    this.menu.selectedIndex = this.select.selectedIndex
  }

  _getField() {
    return this.seedElement || this
  }

  _createOption(option) {
    const menuItem = document.createElement('lui-menuitem')
    if (option.hidden) {
      this.shadowRoot.querySelector('div.lui-select__dialog').classList.add('lui-select__dialogplaceholder')
      menuItem.style.display = 'none'
    }
    menuItem.disabled = option.disabled
    menuItem.className = option.className
    menuItem.autoSelect = true
    menuItem.appendChild(document.createTextNode(option.innerText))

    return menuItem
  }

  generateMenu() {
    const menu = document.createElement('lui-menu')
    menu.setAttribute('slot', 'menu')
    if (this.filterable) {
      this._setMenuPlaceholder(menu)
      menu.setAttribute('filterable', 'filterable')
    }
    if (this.stickySearch) {
      menu.setAttribute('stickysearch', 'stickysearch')
    }
    menu.style.minWidth = '100%'
    Array.from(this.select.options)
      .map(option => this._createOption(option))
      .forEach(item => menu.appendChild(item))

    return menu
  }

  _wireNativeSelect(select) {
    Array.from(select.options).map((option) => {
      this._addPropertyListenerToOption(option)
    })
  }

  _addPropertyListenerToOption(option) {
    this.syncedProperties.forEach((property) => {
      const nativeImplementation = Object.getOwnPropertyDescriptors(Object.getPrototypeOf(option))[property]
      if (!nativeImplementation) return
      Object.defineProperty(option, property, {
        set: (value) => {
          nativeImplementation.set.call(option, value)
          if (!this._ignoreChange) {
            switch (property) {
              case 'selected':
                this.menu.selectedIndex = this.select.selectedIndex
                break
              case 'disabled':
                const matchingIndex = Array.from(this.select.options).indexOf(option)
                this.menu.items[matchingIndex].disabled = value
                break
            }
          }
          this._ignoreChange = false
        },
        get: nativeImplementation.get,
      })
    })
  }

  _createChevron(svgSource, slotName) {
    const div = document.createElement('div')
    div.innerHTML = svgSource
    div.setAttribute('slot', slotName)
    return div
  }

  _updateChevron() {
    const isMenuOpen = this.menu && this.menu.open

    this.classList.toggle('lui-select--open', isMenuOpen)
    this.shadowRoot.querySelector('.lui-select__dialog').classList.toggle('lui-interactive', !isMenuOpen)
    isMenuOpen ? this._showChevronOpened() : this._showChevronClosed()
  }

  get select() {
    return this.querySelector('select')
  }

  get menu() {
    return this._menu
  }

  set menu(element) {
    this._menu = element
  }

  get dialog() {
    this._dialog = this._dialog || this._getDialog()
    return this._dialog
  }

  get chevronClosed() {
    return this.querySelector('div[slot=chevronclosed]')
  }

  get chevronOpened() {
    return this.querySelector('div[slot=chevronopened]')
  }

  get filterable() {
    return this._filterable || (this._filterable = this.hasAttribute('filterable'))
  }

  get stickySearch() {
    return this.filterable && (this._stickySearch || (this._stickySearch = this.hasAttribute('stickysearch')))
  }

  get menuplaceholder() {
    return this._menuplaceholder || (this._menuplaceholder = this.getAttribute('menuplaceholder'))
  }

  _getDialog() {
    return this.shadowRoot.querySelector('.lui-select__dialogwrapper')
  }

  _setDialogHtml(html, hidden = false) {
    this.dialog.innerHTML = html
    this.shadowRoot.querySelector('div.lui-select__dialog')
      .classList[hidden ? 'add' : 'remove']('lui-select__dialogplaceholder')
  }

  _showChevronClosed() {
    if (this.chevronClosed) this.chevronClosed.classList.remove('lui-select__chevronhidden')
    if (this.chevronOpened) this.chevronOpened.classList.add('lui-select__chevronhidden')
  }

  _showChevronOpened() {
    if (this.chevronClosed) this.chevronClosed.classList.add('lui-select__chevronhidden')
    if (this.chevronOpened) this.chevronOpened.classList.remove('lui-select__chevronhidden')
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-select/lui-select.js
// module id = 303
// module chunks = 0 1 14